import React, { useLayoutEffect, useRef } from 'react';
import { NavigationCards } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { ModalConfirm } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import DocumentTitle from 'react-document-title';
import VoucherDetailsHeader from '../../components/VoucherDetailsHeader';
import { TAB_IDS } from './hooks';
import NewTenantEvents from './NewTenantEvents';
import SpecialClaims from './SpecialClaims';
import RepaymentAgreement from './RepaymentAgreement';
import MiscellaneousAccounting from './MiscellaneousAccounting';
import ReviewAndSubmitVoucher from './ReviewAndSubmitVoucher';
import Adjustments from './Adjustments';
import { Stack } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import RegularAssistancePayments from './RegularAssistancePayments';
import ApprovePaymentModal from './ApprovePaymentModal';
import PostPaymentsAndDepositsModal from './PostPaymentsAndDepositsModal';
import messages from './messages';
import { APP_BAR_HEIGHT } from '../../components/TableLayoutV2/constants';
import { useManageHudVoucherDetails } from './hooks.useManageHudVoucherDetails';
import { useVoucherRumContext } from './hooks.useVoucherRumContext';
import useSelectedProperty from '../../hooks/useSelectedProperty';

function ManageHudVoucherDetails({ match }) {
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';

  const {
    intl,
    canManage,
    canSubmit,
    canApprovePayment,
    canPostPayments,
    downloadCertificationMutation,
    hudTenantEvents,
    hudVoucherId,
    propertyId,
    isUndoConfirmOpen,
    effectiveSubmissionStatus,
    isVoucherSubmitted,
    isVoucherLocked,
    isVoucherPaid,
    showPostPaymentsAndDeposits,
    hasUnsubmittedEligibleEvents,
    showSubmittedChangesWarning,
    currentVoucher,
    normalizedTenantEvents,
    tenantEventsLoading,
    voucherLoading,
    voucherStats,
    hasVarianceWarning,
    bucketVarianceFlags,
    tabs,
    tab,
    setTab,
    isVoucherEmpty,
    reviewDetails,
    regularAssistanceViewModel,
    regularAssistanceLoading,
    isMovingToNextVoucher,
    isDownloading52670,
    isSavingApproval,
    isFinalizing,
    isUndoingSubmittedChanges,
    handleVoucherStatusChange,
    refreshVoucherDetails,
    invalidateVoucherDetailQueries,
    handleUndoSubmittedChanges,
    handleCloseUndoConfirm,
    undoConfirmationActionsProps,
    handleSubmitTenantEventsToTracs,
    onMoveToNextVoucher,
    onDownloadHud52670,
    onSaveApproval,
    onFinalizeApproval,
    onMarkVoucherAsPaid,
    onSaveRegularAssistanceApprovedAmount,
    onSaveAdjustmentApprovedAmount,
    setPart6TotalOverride,
  } = useManageHudVoucherDetails({
    match,
    organizationId,
    selectedProperty,
  });

  useVoucherRumContext({
    voucherId: hudVoucherId,
    submissionStatus: voucherLoading ? null : effectiveSubmissionStatus,
    contractNumber: currentVoucher?.contractNumber,
    propertyId,
    tab,
  });

  const scrollContainerRef = useRef(null);

  useLayoutEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [tab]);

  return (
    <DocumentTitle title={intl.formatMessage(messages.voucherDetails)}>
      <Stack
        ref={scrollContainerRef}
        paddingX={2}
        paddingBottom={1}
        sx={{
          height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
          overflowY: 'auto',
          scrollSnapType: 'y proximity',
          '& > *': {
            scrollSnapAlign: 'start',
          },
        }}
      >
        <Stack
          spacing={2}
          sx={{
            minHeight: 'fit-content',
            paddingTop: 1,
            paddingBottom: 2,
          }}
        >
          <VoucherDetailsHeader
            voucher={currentVoucher}
            propertyId={match.params.propertyId}
            showSubmittedChangesWarning={
              showSubmittedChangesWarning && canManage
            }
            showVarianceWarning={hasVarianceWarning}
            onUndoSubmittedChanges={handleUndoSubmittedChanges}
            isUndoingSubmittedChanges={isUndoingSubmittedChanges}
            onDownloadHud52670={onDownloadHud52670}
            isDownloading52670={isDownloading52670}
            isVoucherLoading={voucherLoading}
            voucherActionSlot={
              showPostPaymentsAndDeposits ? (
                <PostPaymentsAndDepositsModal
                  disabled={isVoucherPaid || !canPostPayments}
                  disabledTooltip={
                    !canPostPayments
                      ? intl.formatMessage(messages.noPermissionTooltip)
                      : undefined
                  }
                  onMarkAsPaid={onMarkVoucherAsPaid}
                />
              ) : (
                <ApprovePaymentModal
                  voucher={currentVoucher}
                  voucherStats={voucherStats}
                  disabled={
                    !isVoucherSubmitted || isVoucherLocked || !canApprovePayment
                  }
                  disabledTooltip={
                    !canApprovePayment
                      ? intl.formatMessage(messages.noPermissionTooltip)
                      : undefined
                  }
                  onSaveApproval={onSaveApproval}
                  onFinalizeApproval={onFinalizeApproval}
                  isSaving={isSavingApproval}
                  isFinalizing={isFinalizing}
                />
              )
            }
          />
          <NavigationCards cards={tabs} onChange={setTab} value={tab} />
        </Stack>
        <div>
          <NewTenantEvents
            show={hudTenantEvents && tab === 'new-tenant-events'}
            tenantEvents={normalizedTenantEvents}
            tenantEventsLoading={tenantEventsLoading}
            voucherSubmissionStatus={effectiveSubmissionStatus}
            onMoveToNextVoucher={onMoveToNextVoucher}
            isMovingToNextVoucher={isMovingToNextVoucher}
            onDownloadCertification={(affordableQualificationId, use59A) =>
              downloadCertificationMutation.mutate({
                affordableQualificationId,
                use59A,
              })
            }
            isDownloadingCertification={downloadCertificationMutation.isLoading}
            onSubmitToTracs={handleSubmitTenantEventsToTracs}
            isReadOnly={isVoucherLocked}
            canManage={canManage}
            canSubmit={canSubmit}
          />
          <RegularAssistancePayments
            show={tab === TAB_IDS.REGULAR_ASSISTANCE_PAYMENTS}
            hasVariance={bucketVarianceFlags.regularAssistance}
            isReadOnly={isVoucherLocked}
            onSaveApprovedAmount={onSaveRegularAssistanceApprovedAmount}
            regularAssistancePayments={regularAssistanceViewModel.rows}
            isGated={hasUnsubmittedEligibleEvents}
            hasValidationErrors={regularAssistanceViewModel.hasValidationErrors}
            isLoading={regularAssistanceLoading}
            propertyId={propertyId}
            canManage={canManage}
          />
          <RepaymentAgreement
            show={tab === 'repayment-agr'}
            organizationId={organizationId}
            propertyId={propertyId}
            voucherId={hudVoucherId}
            voucher={currentVoucher}
            voucherSubmissionStatus={effectiveSubmissionStatus}
            onVoucherStatusChange={handleVoucherStatusChange}
            onRefreshVoucher={invalidateVoucherDetailQueries}
            onRepaymentTotalChange={setPart6TotalOverride}
            isReadOnly={isVoucherLocked}
            hasVariance={bucketVarianceFlags.repaymentAgreements}
            canManage={canManage}
          />
          <Adjustments
            show={tab === 'adjustments'}
            organizationId={organizationId}
            propertyId={propertyId}
            voucherId={hudVoucherId}
            voucherSubmissionStatus={effectiveSubmissionStatus}
            isGated={hasUnsubmittedEligibleEvents}
            hasVariance={bucketVarianceFlags.adjustments}
            isReadOnly={isVoucherLocked}
            onSaveApprovedAmount={onSaveAdjustmentApprovedAmount}
            canManage={canManage}
          />
          <SpecialClaims
            show={tab === 'special-claims'}
            organizationId={organizationId}
            propertyId={propertyId}
            voucherId={hudVoucherId}
            voucherSubmissionStatus={effectiveSubmissionStatus}
            onVoucherStatusChange={handleVoucherStatusChange}
            onRefreshVoucher={invalidateVoucherDetailQueries}
            isReadOnly={isVoucherLocked}
            hasVariance={bucketVarianceFlags.specialClaims}
            canManage={canManage}
          />
          <MiscellaneousAccounting
            show={tab === 'misc-acct'}
            organizationId={organizationId}
            propertyId={propertyId}
            voucherId={hudVoucherId}
            voucherSubmissionStatus={effectiveSubmissionStatus}
            onVoucherStatusChange={handleVoucherStatusChange}
            onRefreshVoucher={invalidateVoucherDetailQueries}
            isReadOnly={isVoucherLocked}
            hasVariance={bucketVarianceFlags.miscellaneousAccounting}
            canManage={canManage}
          />
          <ReviewAndSubmitVoucher
            show={tab === 'review-submit-voucher'}
            voucher={currentVoucher}
            reviewDetails={reviewDetails}
            onStatusChange={handleVoucherStatusChange}
            onRefreshVoucher={invalidateVoucherDetailQueries}
            onRefreshVoucherDetails={refreshVoucherDetails}
            isReadOnly={isVoucherSubmitted || isVoucherLocked}
            hasUnsubmittedEligibleEvents={hasUnsubmittedEligibleEvents}
            isVoucherEmpty={isVoucherEmpty}
            submissionBlockers={regularAssistanceViewModel.submissionBlockers}
            organizationId={organizationId}
            propertyId={propertyId}
            canSubmit={canSubmit}
          />
        </div>
        <ModalConfirm
          open={isUndoConfirmOpen}
          title={intl.formatMessage(
            messages.undoSubmittedChangesConfirmationTitle,
          )}
          onClose={handleCloseUndoConfirm}
          ContentProps={{ sx: { maxWidth: 380 } }}
          actionsProps={undoConfirmationActionsProps}
        >
          {intl.formatMessage(messages.undoSubmittedChangesConfirmationBody)}
        </ModalConfirm>
      </Stack>
    </DocumentTitle>
  );
}

export default ManageHudVoucherDetails;
